import { Add as AddIcon, Clear as ClearIcon } from '@mui/icons-material';
import { Box, Paper, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { parse } from 'papaparse';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';

import { Show } from 'src/components/Show';
import { colors } from 'src/global/styles/colors';
import { ProjectInputs } from 'src/global/types/project.type';
import { useDeployment } from 'src/provider/DeploymentProvider';
import { parseConfig, validateCsv } from 'src/utils/template';

import { AdminInput } from '../AdminTextField';
import { DownloadButton } from '../DownloadButton';
import { UploadButton } from '../UploadButton';
import { CustomTableBodyCell, CustomTableHeadCell } from './CustomTableCell';

export function FundingRecipients() {
  const { hasDeployed } = useDeployment();
  const {
    control,
    formState: { errors },
  } = useFormContext<ProjectInputs>();
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'recipients',
  });

  const handleUploadCsvFile = (files: FileList) => {
    const file = files[0];
    const reader = new FileReader();

    reader.onload = async e => {
      try {
        if (!e.target?.result) {
          toast.error('Error reading file');
          return;
        }

        const csv = e.target.result as string;
        const validationRes = validateCsv(csv, {
          headers: ['NAME', 'WALLET_ADDRESS'],
          columnTypes: ['string', 'address'],
        });

        if (!validationRes.isValid) {
          toast.error(validationRes.errors[0]);
          return;
        }

        const parseResult = parse(csv, parseConfig);
        const csvRecipients = parseResult.data
          .slice(1)
          .filter((row: any) => row.length >= 2 && row[0]?.trim() && row[1]?.trim())
          .map((row: any) => ({
            name: row[0].trim(),
            address: row[1].trim(),
          }));

        if (csvRecipients.length === 0) {
          toast.error('No valid recipients found in CSV');
          return;
        }

        // Get the current fields with their IDs
        const currentRecipients = [...fields];

        // Create a new array to hold the updated recipients
        const updatedRecipients = [...currentRecipients];

        // Loop through CSV recipients
        csvRecipients.forEach(csvRecipient => {
          // Check if a recipient with the same name already exists
          const existingIndex = currentRecipients.findIndex(
            existing => existing.name.toLowerCase() === csvRecipient.name.toLowerCase(),
          );

          if (existingIndex >= 0) {
            // Update only the address of the existing recipient
            updatedRecipients[existingIndex] = {
              ...updatedRecipients[existingIndex], // Preserve the id and other properties
              address: csvRecipient.address,
            };
          } else {
            // This is a new recipient - we'll use append() to add it later
            // Don't add it to updatedRecipients here
          }
        });

        // First update existing recipients
        replace(updatedRecipients);

        // Then find and append new recipients
        const newRecipients = csvRecipients.filter(
          csvRecipient =>
            !currentRecipients.some(existing => existing.address.toLowerCase() === csvRecipient.address.toLowerCase()),
        );

        // Append each new recipient individually (this will add the id automatically)
        newRecipients.forEach(newRecipient => {
          append(newRecipient);
        });

        toast.success(`Recipients uploaded successfully`);
      } catch (error) {
        console.error('Error processing CSV:', error);
        toast.error('Error processing CSV file');
      }
    };

    reader.onerror = () => {
      toast.error('Error reading the CSV file');
    };

    reader.readAsText(file);
  };

  return (
    <Box width={'100%'} height={'100%'} display={'flex'} flexDirection={'column'}>
      <Show when={!hasDeployed}>
        <Stack
          spacing={2}
          gap={2}
          flexDirection={{ xs: 'column', sm: 'row' }}
          style={{
            width: 'fit-content',
          }}>
          <UploadButton accept='.csv' label='Import CSV File' multiple={false} uploadCallback={handleUploadCsvFile} />
          <DownloadButton label='Download Template' fileUrl='/resources/files/templates/recipient-template.csv' />
        </Stack>
      </Show>
      <Typography variant='h1' fontWeight={500} fontSize={'16px'} color='white' paddingY={3}>
        Funding Recipients
      </Typography>

      <TableContainer
        component={Paper}
        sx={{ minWidth: 650, backgroundColor: 'transparent !important', color: colors.neutral90 }}>
        <Table
          sx={{ minWidth: 650, backgroundColor: colors.neutral06, color: colors.neutral90 }}
          size='small'
          aria-label='a dense table'>
          <TableHead sx={{ minWidth: 650, backgroundColor: colors.neutral06, color: colors.neutral90, paddingY: 1 }}>
            <TableRow>
              <CustomTableHeadCell width={'30%'}>Name</CustomTableHeadCell>
              <CustomTableHeadCell width={'70%'}>Wallet Address</CustomTableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              background: 'transparent',
              color: 'white',
            }}>
            {fields.map((field, index) => (
              <TableRow
                key={field.id}
                sx={{
                  paddingY: 1,
                  borderBottom: `1px solid ${index === fields.length - 1 ? 'none' : colors.neutral015}`,
                  '&:last-child td, &:last-child th': { border: 0 },
                }}>
                <CustomTableBodyCell>
                  <Controller
                    name={`recipients.${index}.name` as any}
                    control={control}
                    render={({ field }) => (
                      <AdminInput
                        disabled={index === 0 || hasDeployed}
                        isError={!!errors?.recipients?.[index]?.name}
                        errorMsg={errors?.recipients?.[index]?.name?.message}
                        placeholder={'Enter name'}
                        focusedBorderColor='white'
                        borderColor={
                          errors?.recipients?.[index]?.name ? colors.error : !field.value ? 'white' : 'transparent'
                        }
                        {...field}
                      />
                    )}
                  />
                </CustomTableBodyCell>
                <CustomTableBodyCell>
                  <div className='flex justify-between items-start'>
                    <Box width={'-webkit-fill-available'}>
                      <Controller
                        name={`recipients.${index}.address` as any}
                        control={control}
                        render={({ field }) => (
                          <AdminInput
                            disabled={index === 0 || hasDeployed}
                            isError={!!errors?.recipients?.[index]?.address}
                            errorMsg={errors?.recipients?.[index]?.address?.message}
                            placeholder={'Enter Wallet Address'}
                            focusedBorderColor='white'
                            borderColor={
                              errors?.recipients?.[index]?.address
                                ? colors.error
                                : !field.value
                                  ? 'white'
                                  : 'transparent'
                            }
                            style={{
                              width: '-webkit-fill-available',
                            }}
                            {...field}
                          />
                        )}
                      />
                    </Box>
                    <Show when={!hasDeployed}>
                      <Stack direction='row' spacing={1} alignItems={'center'} marginLeft={'10px'} marginTop={'12px'}>
                        {index === fields.length - 1 && (
                          <AddIcon onClick={() => append({ name: '', address: '' })} className='cursor-pointer' />
                        )}
                        {fields.length > 1 && index > 0 && (
                          <ClearIcon
                            onClick={() => remove(index)}
                            className='cursor-pointer'
                            sx={{
                              color: colors.error,
                            }}
                          />
                        )}
                      </Stack>
                    </Show>
                  </div>
                </CustomTableBodyCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
